// Helper function to add date ordinal
import DateTimeUtils from './DateTimeUtils';
import _ from 'lodash';

// Removes timezone from Date received as UTC without timezone
const adjustDateToTimezone = (date: Date) => {
  const dateInMilliseconds = date.getTime();
  const timeZoneDifferenceInMinutes = date.getTimezoneOffset();
  const timeZoneInMilliseconds = timeZoneDifferenceInMinutes * 60 * 1000;
  const differenceInMilliseconds = dateInMilliseconds - timeZoneInMilliseconds;
  return new Date(differenceInMilliseconds);
};

// Removes timezone from local Date received to UTC
const adjustDateToUTC = (date: Date) => {
  const dateInMilliseconds = date.getTime();
  const timeZoneDifferenceInMinutes = date.getTimezoneOffset();
  const timeZoneInMilliseconds = timeZoneDifferenceInMinutes * 60 * 1000;
  const differenceInMilliseconds = dateInMilliseconds + timeZoneInMilliseconds;
  return new Date(differenceInMilliseconds);
};

// Formats the received ISO date into a more user-friendly format
const isoToNowCuriefenseFormat = (date: string | Date) => {
  if (typeof date === 'string') {
    date = new Date(date);
  }
  const currentDate = new Date();
  const timeSuffix = currentDate.getTime() > date.getTime() ? ' ago' : '';
  const timePrefix = currentDate.getTime() > date.getTime() ? '' : 'in';
  const dateDiff = Math.abs(currentDate.getTime() - date.getTime());
  const hoursDiff = dateDiff / 36e5;
  const minutesDiff = dateDiff / 6e4;
  const flooredHoursDiff = Math.floor(hoursDiff);
  const flooredMinutesDiff = Math.floor(minutesDiff);
  let returnString = '';
  if (timePrefix) {
    returnString += timePrefix;
  }
  // if up to a minute
  if (!flooredHoursDiff && flooredMinutesDiff <= 1) {
    if (returnString) {
      returnString += ' ';
    }
    returnString += `a minute`;
    returnString += timeSuffix;
    return _.capitalize(returnString);
  }
  // if less than 24 hours
  if (hoursDiff < 24) {
    if (flooredHoursDiff > 0) {
      if (returnString) {
        returnString += ' ';
      }
      returnString += `${flooredHoursDiff} hour`;
      if (flooredHoursDiff > 1) {
        returnString += 's';
      }
    }
    if (flooredMinutesDiff % 60 > 0) {
      if (returnString) {
        returnString += ' ';
      }
      returnString += `${flooredMinutesDiff % 60} minute`;
      if (flooredMinutesDiff % 60 > 1) {
        returnString += 's';
      }
    }
    returnString += timeSuffix;
    return _.capitalize(returnString);
  }
  return DateTimeUtils.isoToNowFullCuriefenseFormat(date);
};

// Full date formatted display
const isoToNowFullCuriefenseFormat = (date: string | Date) => {
  if (typeof date === 'string') {
    date = new Date(date);
  }
  const year = date.getFullYear();
  const month = date.getMonth() + 1 < 10 ? `0${date.getMonth() + 1}` : date.getMonth() + 1;
  const day = date.getDate() < 10 ? `0${date.getDate()}` : date.getDate();
  const hours = date.getHours() < 10 ? `0${date.getHours()}` : date.getHours();
  const minutes = date.getMinutes() < 10 ? `0${date.getMinutes()}` : date.getMinutes();
  const seconds = date.getSeconds() < 10 ? `0${date.getSeconds()}` : date.getSeconds();
  return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
};

// Formats date as yyyy-MM-dd HH:mm:ss, but only shows seconds if they are not 0
const formatDateWithConditionalSeconds = (date: string | Date) => {
  if (typeof date === 'string') {
    date = new Date(date);
  }
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const seconds = date.getSeconds();
  const secondsString = seconds > 0 ? `:${String(seconds).padStart(2, '0')}` : '';
  return `${year}-${month}-${day} ${hours}:${minutes}${secondsString}`;
};

const dateToMonthName = (date: Date) => {
  const monthNames = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ];
  return monthNames[date.getMonth()];
};

// Format date to "YYYY-MM-DD HH:mm" without seconds
const formatDateTime = (date: string | Date) => {
  if (!date) return 'N/A';
  try {
    if (typeof date === 'string') {
      date = new Date(date);
    }
    // Check if date is valid
    if (isNaN(date.getTime())) {
      return 'N/A';
    }
    const year = date.getFullYear();
    const month = date.getMonth() + 1 < 10 ? `0${date.getMonth() + 1}` : date.getMonth() + 1;
    const day = date.getDate() < 10 ? `0${date.getDate()}` : date.getDate();
    const hours = date.getHours() < 10 ? `0${date.getHours()}` : date.getHours();
    const minutes = date.getMinutes() < 10 ? `0${date.getMinutes()}` : date.getMinutes();
    return `${year}-${month}-${day} ${hours}:${minutes}`;
  } catch {
    return 'N/A';
  }
};

export default {
  name: 'DateTimeUtils',
  adjustDateToTimezone,
  adjustDateToUTC,
  isoToNowCuriefenseFormat,
  isoToNowFullCuriefenseFormat,
  formatDateWithConditionalSeconds,
  dateToMonthName,
  formatDateTime,
};
